// src/pages/configuracion/components/RolFormModal.tsx
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Checkbox,
} from '@/components/ui';
import { toast } from '@/components/ui/Toast';
import { Save, Search } from 'lucide-react';
import { useCrearRol, useActualizarRol, usePermisos } from '@/hooks/queries/useConfiguracion';
import type { RolAdmin } from '@/types';

const schema = z.object({
  codigo: z.string()
    .min(2, 'El codigo debe tener al menos 2 caracteres')
    .regex(/^[A-Z_]+$/, 'El codigo debe ser en mayusculas sin espacios'),
  nombre: z.string().min(1, 'El nombre es requerido'),
  descripcion: z.string().optional(),
  permisos: z.array(z.string()),
});

type FormData = z.infer<typeof schema>;

interface RolFormModalProps {
  open: boolean;
  onClose: () => void;
  rol: RolAdmin | null;
}

// Grupos de permisos predefinidos
const GRUPOS_PERMISOS = {
  'Clientes': ['clientes:ver', 'clientes:crear', 'clientes:editar', 'clientes:eliminar'],
  'Tickets': ['tickets:ver', 'tickets:crear', 'tickets:editar', 'tickets:eliminar', 'tickets:asignar'],
  'Conversaciones': ['conversaciones:ver', 'conversaciones:crear', 'conversaciones:responder'],
  'Proyectos': ['proyectos:ver', 'proyectos:crear', 'proyectos:editar', 'proyectos:eliminar'],
  'Conocimiento': ['conocimiento:ver', 'conocimiento:crear', 'conocimiento:editar', 'conocimiento:publicar'],
  'Reportes': ['reportes:ver', 'reportes:crear', 'reportes:exportar'],
  'Configuración': ['config:ver', 'config:usuarios', 'config:roles', 'config:sistema'],
};

export function RolFormModal({ open, onClose, rol }: RolFormModalProps) {
  const isEditing = !!rol;
  const [searchPermiso, setSearchPermiso] = useState('');
  const { data: permisosDisponibles } = usePermisos();
  const crearMutation = useCrearRol();
  const actualizarMutation = useActualizarRol();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      codigo: '',
      nombre: '',
      descripcion: '',
      permisos: [],
    },
  });

  useEffect(() => {
    if (rol) {
      form.reset({
        codigo: rol.codigo,
        nombre: rol.nombre,
        descripcion: rol.descripcion ?? '',
        permisos: rol.permisos,
      });
    } else {
      form.reset({
        codigo: '',
        nombre: '',
        descripcion: '',
        permisos: [],
      });
    }
  }, [rol, form]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing && rol) {
        await actualizarMutation.mutateAsync({
          id: rol.id,
          data: {
            nombre: data.nombre,
            descripcion: data.descripcion,
            permisos: data.permisos,
          },
        });
        toast.success('Rol actualizado correctamente');
      } else {
        await crearMutation.mutateAsync(data);
        toast.success('Rol creado correctamente');
      }
      onClose();
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const selectedPermisos = form.watch('permisos') || [];

  const togglePermiso = (permiso: string) => {
    if (selectedPermisos.includes(permiso)) {
      form.setValue('permisos', selectedPermisos.filter(p => p !== permiso));
    } else {
      form.setValue('permisos', [...selectedPermisos, permiso]);
    }
  };

  const toggleGrupo = (grupo: string) => {
    const permisosGrupo = GRUPOS_PERMISOS[grupo as keyof typeof GRUPOS_PERMISOS] || [];
    const todosSeleccionados = permisosGrupo.every(p => selectedPermisos.includes(p));

    if (todosSeleccionados) {
      form.setValue('permisos', selectedPermisos.filter(p => !permisosGrupo.includes(p)));
    } else {
      const nuevos = permisosGrupo.filter(p => !selectedPermisos.includes(p));
      form.setValue('permisos', [...selectedPermisos, ...nuevos]);
    }
  };

  const filteredPermisos = permisosDisponibles?.filter(p =>
    p.toLowerCase().includes(searchPermiso.toLowerCase())
  ) || Object.values(GRUPOS_PERMISOS).flat();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Código"
              {...form.register('codigo')}
              error={form.formState.errors.codigo?.message}
              disabled={isEditing && rol?.es_sistema}
              placeholder="NOMBRE_ROL"
            />
            <Input
              label="Nombre"
              {...form.register('nombre')}
              error={form.formState.errors.nombre?.message}
            />
          </div>

          <Textarea
            label="Descripción"
            {...form.register('descripcion')}
            rows={2}
            placeholder="Descripción opcional del rol..."
          />

          {/* Permisos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permisos ({selectedPermisos.length} seleccionados)
            </label>

            {/* Buscar */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar permisos..."
                value={searchPermiso}
                onChange={(e) => setSearchPermiso(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Grupos rapidos */}
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.keys(GRUPOS_PERMISOS).map((grupo) => {
                const permisosGrupo = GRUPOS_PERMISOS[grupo as keyof typeof GRUPOS_PERMISOS];
                const todosSeleccionados = permisosGrupo.every(p => selectedPermisos.includes(p));
                const algunoSeleccionado = permisosGrupo.some(p => selectedPermisos.includes(p));

                return (
                  <button
                    key={grupo}
                    type="button"
                    onClick={() => toggleGrupo(grupo)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      todosSeleccionados
                        ? 'bg-blue-600 text-white'
                        : algunoSeleccionado
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {grupo}
                  </button>
                );
              })}
            </div>

            {/* Lista de permisos */}
            <div className="border rounded-md max-h-48 overflow-y-auto p-3 space-y-2">
              {filteredPermisos.map((permiso) => (
                <label key={permiso} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <Checkbox
                    checked={selectedPermisos.includes(permiso)}
                    onCheckedChange={() => togglePermiso(permiso)}
                  />
                  <span className="text-sm text-gray-700">{permiso}</span>
                </label>
              ))}
              {filteredPermisos.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2">
                  No se encontraron permisos
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={crearMutation.isPending || actualizarMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
